import { LS_AUTHORITY_KEY, USER_ROLE } from "../../../../core/constants/common";
import { HIERARCHY_ROLES } from "../constants/hierarchyRoles";
import {
  getAvailableDashboardViews,
  getDefaultDashboardViewId
} from "./hierarchyDashboardViews";
import { getAllowedSidebarNavKeys, hasGoalsKpiNavAccess } from "./goalsAndKpiNavAccess";

/** Read authority blob — same shape as UserContext / localStorage `authority`. */
export const getHierarchyAuth = () => {
  try {
    const raw = localStorage.getItem(LS_AUTHORITY_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const getHierarchyUserRoles = (auth = getHierarchyAuth()) => {
  const roles = auth?.roles;
  return Array.isArray(roles) ? roles : [];
};

export const getHierarchyUserId = (auth = getHierarchyAuth()) => {
  const id =
    auth?.profile?.user?.id ??
    auth?.profile?.id ??
    auth?.user?.id ??
    auth?.userId ??
    auth?.id;
  return id != null ? String(id) : null;
};

export const hasHierarchyRole = (roles, role) =>
  Array.isArray(roles) && roles.includes(role);

/** Module sidebar / route entry — VIEW_GOALS_KPI (or legacy UPDATE_GOALS_KPI). */
export const canAccessGoalsKpiModule = (roles) =>
  hasHierarchyRole(roles, USER_ROLE.GOALS_AND_KPIS_VIEW) ||
  hasHierarchyRole(roles, USER_ROLE.GOALS_AND_KPIS_UPDATE);

export const canViewAllNodes = (roles) =>
  hasHierarchyRole(roles, HIERARCHY_ROLES.VIEW_ALL_HIERARCHY);

export const canViewDepartmentHierarchy = (roles) =>
  hasHierarchyRole(roles, HIERARCHY_ROLES.VIEW_DEPARTMENT_HIERARCHY);

export const canViewDivisionHierarchy = (roles) =>
  hasHierarchyRole(roles, HIERARCHY_ROLES.VIEW_DIVISION_HIERARCHY);

export const canViewAssignedHierarchy = (roles) =>
  hasHierarchyRole(roles, HIERARCHY_ROLES.VIEW_ASSIGNED_HIERARCHY);

export const canViewChampionHierarchy = (roles) =>
  hasHierarchyRole(roles, HIERARCHY_ROLES.VIEW_CHAMPION_HIERARCHY);

export const canManageMetadata = (roles) =>
  hasHierarchyRole(roles, HIERARCHY_ROLES.MANAGE_HIERARCHY_METADATA);

/** FE gate for progress/measure Approve/Reject UI — Champion or metadata Admin only. */
export const canDecideAsChampionOrAdmin = (roles) =>
  canViewChampionHierarchy(roles) || canManageMetadata(roles);

export const canManageAssignments = (roles) =>
  hasHierarchyRole(roles, HIERARCHY_ROLES.MANAGE_HIERARCHY_ASSIGNMENTS);

/** Full org Audit History — VIEW_ALL_HIERARCHY alone does not qualify. */
export const canViewFullAudit = (roles) =>
  hasHierarchyRole(roles, HIERARCHY_ROLES.VIEW_ALL_HIERARCHY_AUDIT) ||
  hasHierarchyRole(roles, HIERARCHY_ROLES.MANAGE_HIERARCHY_METADATA);

/** CEO-style read-only plan list/detail — no MANAGE_HIERARCHY_METADATA required. */
export const canViewPlansReadOnly = (roles) =>
  canManageMetadata(roles) || canViewAllNodes(roles);

/** Admin full CRUD, CEO read-only list/editor. Hidden from every other hierarchy role. */
export const canAccessAlertRules = (roles) =>
  canManageMetadata(roles) || canViewAllNodes(roles);

export const hasHigherVisibilityThanTaskOwner = (roles) =>
  canManageMetadata(roles) ||
  canViewAllNodes(roles) ||
  canViewDepartmentHierarchy(roles) ||
  canViewDivisionHierarchy(roles) ||
  canViewChampionHierarchy(roles);

/** Task Owner group only — no broader visibility JWT roles. */
export const isTaskOwnerOnlyScope = (roles) =>
  canViewAssignedHierarchy(roles) && !hasHigherVisibilityThanTaskOwner(roles);

export const isNodeAssignee = (assignments, nodeId, userId) => {
  if (!nodeId || !userId) {
    return false;
  }
  const nid = String(nodeId);
  const uid = String(userId);
  return (assignments || []).some((a) => String(a.nodeId) === nid && String(a.userId) === uid);
};

export const isPrimaryAssignee = (assignments, nodeId, userId) => {
  if (!nodeId || !userId) {
    return false;
  }
  const nid = String(nodeId);
  const uid = String(userId);
  return (assignments || []).some(
    (a) => String(a.nodeId) === nid && String(a.userId) === uid && a.isPrimary
  );
};

export const isApproverOnNode = (assignments, nodeId, userId) => {
  if (!nodeId || !userId) {
    return false;
  }
  const nid = String(nodeId);
  const uid = String(userId);
  return (assignments || []).some(
    (a) =>
      String(a.nodeId) === nid &&
      String(a.userId) === uid &&
      (a.assignmentType === "APPROVER" || a.role === "Approver" || a.role === "APPROVER")
  );
};

const getAncestorNodeIds = (nodes, nodeId) => {
  const ids = [];
  let current = (nodes || []).find((n) => n.id === nodeId);
  while (current?.parentNodeId) {
    const parentId = current.parentNodeId;
    ids.push(String(parentId));
    current = (nodes || []).find((n) => n.id === parentId);
  }
  return ids;
};

/**
 * Mirrors HierarchyAccessSupport.canApproveNode (backend, single source of truth for both the
 * queue and the decide gate): APPROVER on the node itself or any ancestor, OR PRIMARY/CO_OWNER on
 * an ancestor ONLY — a node's own PRIMARY/CO_OWNER has no approval standing on their own
 * submission. `nodes` is required to walk the ancestor chain; without it this degrades to
 * node-only APPROVER matching.
 */
export const canDecideApproval = (args, approvalArg, userIdArg) => {
  let roles, assignments, approval, userId, nodes;
  if (args && typeof args === "object" && !Array.isArray(args)) {
    roles = args.roles;
    assignments = args.assignments;
    approval = args.approval;
    userId = args.userId;
    nodes = args.nodes;
  } else {
    assignments = args;
    approval = approvalArg;
    userId = userIdArg;
  }

  if (canManageMetadata(roles) || hasHierarchyRole(roles, USER_ROLE.SUPER_ADMIN)) {
    return true;
  }
  if (!approval?.nodeId) {
    return false;
  }
  if (isApproverOnNode(assignments, approval.nodeId, userId)) {
    return true;
  }
  const ancestorIds = getAncestorNodeIds(nodes, approval.nodeId);
  return ancestorIds.some(
    (ancestorId) =>
      isApproverOnNode(assignments, ancestorId, userId) ||
      isPrimaryOrCoOwnerAssignee(assignments, ancestorId, userId)
  );
};

export const canDecideAnyApproval = (args, approvalsArg, userIdArg) => {
  let roles, assignments, approvals, userId, nodes;
  if (args && typeof args === "object" && !Array.isArray(args)) {
    roles = args.roles;
    assignments = args.assignments;
    approvals = args.approvals;
    userId = args.userId;
    nodes = args.nodes;
  } else {
    assignments = args;
    approvals = approvalsArg;
    userId = userIdArg;
  }

  if (!Array.isArray(approvals)) {
    return false;
  }
  return approvals.some(
    (approval) =>
      approval.status === "PENDING" &&
      canDecideApproval({ roles, assignments, approval, userId, nodes })
  );
};

const COMMENT_THREAD_START_TYPES = new Set(["PRIMARY", "CO_OWNER", "REVIEWER", "APPROVER"]);
const COMMENT_REPLY_TYPES = new Set(["PRIMARY", "CO_OWNER", "REVIEWER"]);

const hasAssignmentTypesOnNode = (assignments, nodeId, userId, types) => {
  if (!nodeId || !userId) {
    return false;
  }
  const nid = String(nodeId);
  const uid = String(userId);
  return (assignments || []).some(
    (a) =>
      String(a.nodeId) === nid &&
      String(a.userId) === uid &&
      types.has(a.assignmentType)
  );
};

const hasAssignmentTypesOnNodeOrAncestor = (assignments, nodes, nodeId, userId, types) => {
  if (hasAssignmentTypesOnNode(assignments, nodeId, userId, types)) {
    return true;
  }
  return getAncestorNodeIds(nodes, nodeId).some((ancestorId) =>
    hasAssignmentTypesOnNode(assignments, ancestorId, userId, types)
  );
};

/**
 * Backend requireCommentThreadStart: MANAGE_HIERARCHY_METADATA, or PRIMARY/CO_OWNER/REVIEWER/
 * APPROVER on this node or any ancestor. Gates the Messages tab and the new-conversation composer.
 */
export const hasCommentThreadStartAssignmentOnNode = (assignments, nodeId, userId) =>
  hasAssignmentTypesOnNode(assignments, nodeId, userId, COMMENT_THREAD_START_TYPES);

export const canStartCommentThread = ({ roles, assignments, nodes, nodeId, userId }) => {
  if (canManageMetadata(roles)) {
    return true;
  }
  if (!nodeId || !userId) {
    return false;
  }
  return hasAssignmentTypesOnNodeOrAncestor(
    assignments,
    nodes,
    nodeId,
    userId,
    COMMENT_THREAD_START_TYPES
  );
};

/**
 * Backend requireCommentReply: MANAGE_HIERARCHY_METADATA, or PRIMARY/CO_OWNER/REVIEWER on this
 * node or any ancestor. APPROVER is excluded. Gates the per-message reply action.
 */
export const canReplyToComment = ({ roles, assignments, nodes, nodeId, userId }) => {
  if (canManageMetadata(roles)) {
    return true;
  }
  if (!nodeId || !userId) {
    return false;
  }
  return hasAssignmentTypesOnNodeOrAncestor(assignments, nodes, nodeId, userId, COMMENT_REPLY_TYPES);
};

const CONTENT_EDITOR_ASSIGNMENT_TYPES = new Set(["PRIMARY", "CO_OWNER"]);

/** PRIMARY or CO_OWNER on a specific node — not REVIEWER/APPROVER. */
export const isPrimaryOrCoOwnerAssignee = (assignments, nodeId, userId) => {
  if (!nodeId || !userId) {
    return false;
  }
  const nid = String(nodeId);
  const uid = String(userId);
  return (assignments || []).some(
    (a) =>
      String(a.nodeId) === nid &&
      String(a.userId) === uid &&
      CONTENT_EDITOR_ASSIGNMENT_TYPES.has(a.assignmentType)
  );
};

/**
 * Legacy Path A check with ancestor PRIMARY/CO_OWNER walk. Do not use for Create/Edit/Delete/
 * Reallocate UI (those are metadata-admin-only) or for Submit Update / Record Actuals (use
 * canEditNodeDirectly). Kept for any call sites that still need the broader standing.
 */
export const canEditNode = ({ roles, assignments, nodes, nodeId, userId }) => {
  if (canManageMetadata(roles) || hasHierarchyRole(roles, USER_ROLE.SUPER_ADMIN)) {
    return true;
  }
  if (!nodeId || !userId) {
    return false;
  }
  if (isPrimaryOrCoOwnerAssignee(assignments, nodeId, userId)) {
    return true;
  }
  if (!Array.isArray(nodes) || !nodes.length) {
    return false;
  }
  return getAncestorNodeIds(nodes, nodeId).some((ancestorId) =>
    isPrimaryOrCoOwnerAssignee(assignments, ancestorId, userId)
  );
};

/**
 * Progress / measure-actuals UI standing — matches backend requireNodeEdit for those actions:
 * metadata admin, platform super admin, or PRIMARY/CO_OWNER on this node only (no ancestor walk).
 */
export const canEditNodeDirectly = ({ roles, assignments, nodeId, userId }) => {
  if (canManageMetadata(roles) || hasHierarchyRole(roles, USER_ROLE.SUPER_ADMIN)) {
    return true;
  }
  return isPrimaryOrCoOwnerAssignee(assignments, nodeId, userId);
};

/**
 * Create-child UI — metadata admin only. Deliberately stricter than the backend (which also
 * allows parent assignees); product decision so non-admin owners never see Create controls.
 */
export const canCreateChildUnderNode = ({ roles }) => canManageMetadata(roles);

/**
 * Additional-parent link mutations (create link, remove link, edit link weight) — matches the
 * backend's own restriction for these actions exactly: metadata admin or MANAGE_HIERARCHY_NODE_CONTENT.
 */
export const canManageHierarchyLinks = (roles) =>
  canManageMetadata(roles) || hasHierarchyRole(roles, HIERARCHY_ROLES.MANAGE_HIERARCHY_NODE_CONTENT);

/**
 * Delete-node UI — metadata admin (or platform super admin) only, and only while the plan is
 * still Draft. Stricter than backend ownership rules; same product decision as Create/Edit.
 */
export const canDeleteNode = ({ roles, plan }) => {
  if (plan?.status !== "DRAFT") {
    return false;
  }
  return canManageMetadata(roles) || hasHierarchyRole(roles, USER_ROLE.SUPER_ADMIN);
};

export const buildHierarchyPermissions = (roles) => {
  const availableDashboardViews = getAvailableDashboardViews(roles);
  const base = {
    roles,
    canAccessModule: canAccessGoalsKpiModule(roles),
    canViewAllNodes: canViewAllNodes(roles),
    canViewDepartmentHierarchy: canViewDepartmentHierarchy(roles),
    canViewDivisionHierarchy: canViewDivisionHierarchy(roles),
    canViewAssignedHierarchy: canViewAssignedHierarchy(roles),
    canViewChampionHierarchy: canViewChampionHierarchy(roles),
    canManageMetadata: canManageMetadata(roles),
    canManageAssignments: canManageAssignments(roles),
    canViewFullAudit: canViewFullAudit(roles),
    canViewPlansReadOnly: canViewPlansReadOnly(roles),
    canAccessAlertRules: canAccessAlertRules(roles),
    isTaskOwnerOnlyScope: isTaskOwnerOnlyScope(roles),
    availableDashboardViews,
    defaultDashboardViewId: getDefaultDashboardViewId(roles)
  };
  const allowedSidebarNavKeys = getAllowedSidebarNavKeys(base);
  return {
    ...base,
    allowedSidebarNavKeys: [...allowedSidebarNavKeys],
    hasGoalsKpiNavAccess: hasGoalsKpiNavAccess(base)
  };
};
